import type {User} from '../auth';
import {profilesMatchPortalType, type PortalProfileShape} from '@common/portalAccess';

/**
 * Identifies which portal a frontend app is running as. `internal` is the CBS
 * staff portal; `client` is a per-tenant portal scoped to a single `clientId`.
 */
export type PortalInfo =
  | {portalType: 'internal'}
  | {portalType: 'client'; clientId: string; clientName?: string};

/** Reduce an enriched session user to the booleans a portal decision needs. */
export function portalProfileShape(user: User): PortalProfileShape {
  return {
    hasInternalProfile: !!user.cbsInternalProfile,
    hasClientProfile: !!user.clientProfile,
    hasBrokerProfile: !!user.brokerProfile,
    hasTpaProfile: !!user.tpaProfile,
    hasGroupProfile: !!(user.groupProfiles && user.groupProfiles.length > 0),
  };
}

/**
 * Whether `user` is allowed to operate in `portal`.
 *
 * All portals that call the API directly share one API session cookie, so a
 * single sign-in can be presented to any portal. This guard ensures a session
 * is only accepted by a portal of the matching *type* (by profile shape),
 * preventing an internal login from "bleeding" into a client portal and vice
 * versa. A null/absent portal (non-portal surface) is never filtered.
 *
 * This is a pure function: no side effects, no module-global state. It is the
 * single source of truth used by every auth guard and login page.
 */
export function userMatchesPortal(user: User, portal: PortalInfo | null | undefined): boolean {
  if (!portal) return true;

  return profilesMatchPortalType(portalProfileShape(user), portal.portalType);
}
